import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import gql from 'graphql-tag';
import { withRouter, WithRouterProps } from 'react-router';
import { Location } from 'history';
import ConditionDisplay from './condition_display';
import PriceDisplay from './price_display';
import PriceWithShipping from './price_with_shipping';
import ImageCarousel from './image_carousel';
import ListingCardNudge from './listing_card_nudge';
import RibbonView from './ribbon_view';
import {
  ListingCardFields,
  FinancingFields,
  InOtherCartsCardData,
  core_apimessages_Listing_ListingType as ListingType,
  core_apimessages_Address as Address,
} from '../rql';
import AddToCartButton from './add_to_cart_button';
import WatchBadgeWithRQL from './watch_badge';
import CoreLink from './core_link';
import { ITEM_WITH_SLUG_ROUTE, listingItemPagePath } from '../url_helpers';
import { withUserContext, IUserContext, IUser } from './user_context_provider';
import BumpTag, { shouldShowBumpTag } from './bump_tag';
import { buildListingClickEvent } from '../elog/mparticle_tracker';
import { WEB } from '../constants';
import experiments from '../experiments';
import { formatExperimentsForEventAttributes } from '../elog/mparticle';
import { ListingOfferMetadata } from './listing_offer_metadata';
import { InOtherCartsCardNudge } from './in_other_carts_card_nudge';
import ListingShopCountryNudge from './listing_shop_country_nudge';
import { experimentInt, isExperimentEnabled } from '../user_context_helpers';
import { BRAND_NEW } from '../condition_helpers';

const DISPLAY_STYLE = 'squareCard';

type ListingCardListingFields = ListingCardFields.Fragment & FinancingFields.Fragment & InOtherCartsCardData.Fragment;

interface IProps {
  // This type is likely _more_ than what this component needs.
  // Cutting a fragment for this component would help better communicate
  // the contract here.
  listing?: ListingCardListingFields;
  position?: number;
  inMobileApp?: boolean;
  soldListing?: boolean;
  footerContent?: React.ReactNode;
  hideMetadataAsOffer?: boolean;
  trackingName?: string;
  displayAddToCart?: boolean;
  listingsCount?: number;
  combinedShippingDiscount?: boolean;
  showEstimatedMonthlyPayment?: boolean;
  redesignedCard?: boolean;
  trackingQuery?: string;
  showFlag?: boolean;
}

export class ListingCard extends React.Component<IProps & IUserContext & WithRouterProps, null> {
  static defaultProps = {
    showFlag: true,
  };

  isFreeExpeditedShipping() {
    return !!this.props.listing.shipping?.freeExpeditedShipping;
  }

  isFreeShipping() {
    return this.props.listing.shipping?.shippingPrices?.[0]?.rate?.amountCents === 0;
  }

  isLocalPickupOnly() {
    return !!this.props.listing.shipping?.localPickupOnly;
  }

  isLocalPickupAvailable() {
    const { listing: { shipping, shop }, user, location } = this.props;
    const localPickupAvailable = !!shipping?.localPickup && !this.isLocalPickupOnly();
    const shopLocalExpBucket = experimentInt(user, experiments.SHOP_LOCAL_V2);
    if (!localPickupAvailable) return false;
    if (!showProximityContent(shop?.address, user, location)) return false;
    if (shopLocalExpBucket === 0) return false;
    return true;
  }

  isGreatValue() {
    const { listing: { pricing, priceRecommendation } } = this.props;

    const middlePrice = priceRecommendation?.priceMiddle?.amount;
    return !!middlePrice && pricing.buyerPrice.amount <= middlePrice;
  }

  returnPolicyDays() {
    const { listing: { condition, shop } } = this.props;
    const returnPolicyDays = condition.conditionSlug === BRAND_NEW ? shop.returnPolicy?.newReturnWindowDays : shop.returnPolicy?.usedReturnWindowDays;
    return (returnPolicyDays || 0);
  }

  listingUrl() {
    if (this.props.soldListing) {
      return (
        ITEM_WITH_SLUG_ROUTE.expand({
          slug: this.props.listing.slug,
          id: this.props.listing.id,
          show_sold: true,
        })
      );
    }

    return (
      listingItemPagePath(this.props.listing)
    );
  }

  hasDiscount() {
    return !!this.props.listing.pricing.originalPrice;
  }

  ribbonText() {
    return this.props.listing.pricing.ribbon ?
      this.props.listing.pricing.ribbon.display :
      null;
  }

  renderOriginalPrice() {
    if (!this.hasDiscount()) { return null; }

    return (
      <div className="grid-card__original-price">
        <span className="mr-space td-line-through">
          {this.props.listing.pricing.originalPrice.display}
        </span>
        {this.props.redesignedCard &&
          <span className="grid-card__discount">
            {this.ribbonText()}
          </span>
        }
      </div>
    );
  }

  renderAffirmCallout() {
    const { showEstimatedMonthlyPayment, listing } = this.props;
    const estimatedMonthlyPayment = listing.estimatedMonthlyPaymentPlan?.estimatedMonthlyPayment?.display;
    if (!showEstimatedMonthlyPayment || !estimatedMonthlyPayment) { return null; }

    return (
      <div className="grid-card__affirm-callout">
        {I18n.t('commons.estimatedMonthlyPaymentCallout', { displayPrice: estimatedMonthlyPayment })}
      </div>
    );
  }

  renderShippingCallout() {
    if (isDigital(this.props.listing) || this.props.combinedShippingDiscount) {
      return null;
    }

    if (this.isFreeExpeditedShipping()) {
      return (
        <div
          className="grid-card__free-2-day"
        >
          {I18n.t('cms.freeExpeditedShipping')}
        </div>
      );
    }

    if (this.isFreeShipping()) {
      return (
        <div
          className="grid-card__free-shipping"
        >
          {I18n.t('commons.freeShipping')}
        </div>
      );
    }

    if (this.isLocalPickupOnly()) {
      return (
        <div
          className="grid-card__local-pickup"
        >
          {I18n.t('commons.localPickupOnly')}
        </div>
      );
    }

    return false;
  }

  renderMetadata() {
    if (this.props.hideMetadataAsOffer) return null;

    return (
      <ListingOfferMetadata listing={this.props.listing} />
    );
  }

  clickEvent() {
    return {
      experiments: formatExperimentsForEventAttributes(
        this.props.user,
        [
          experiments.LISTING_CARD_LOCATION,
          experiments.DUMMY,
          experiments.DATA_DRIVEN_WELL_PRICED_LISTINGS,
        ],
      ),
      query: this.props.trackingQuery,
      ...buildListingClickEvent({
        listing: this.props.listing,
        componentName: this.props.trackingName,
        displayStyle: DISPLAY_STYLE,
        position: this.props.position,
        listingsCount: this.props.listingsCount,
        city: this.props.listing.shop.address?.locality,
        region: this.props.listing.shop.address?.region,
        countryCode: this.sellerCountryCode,
        isListingDomesticToBuyer: isListingDomesticToBuyer(this.sellerCountryCode, this.props.user?.countryCode),
      }),
    };
  }

  get sellerCountryCode() {
    return this.props.listing.shop.address?.country?.countryCode;
  }

  renderProximityShopLocation() {
    const shopAddress = this.props.listing.shop?.address;
    if (!shopAddress) return null;

    if (showProximityContent(shopAddress, this.props.user, this.props.location)) {
      return (
        <div data-proximity-location className="size-80 color-secondary">
          {I18n.t('commons.listingCard.location.sameCountryUs', {
            city: shopAddress.locality, state: shopAddress.region,
          })}
        </div>
      );
    }

    return null;
  }

  renderShopLocationCountryFlag() {
    const shopAddress = this.props.listing.shop?.address;
    if (!shopAddress || !shopAddress.country) return null;

    if (this.props.showFlag && showShopCountryNudge(this.props.user, this.props.location)) {
      return (
        <div>
          <ListingShopCountryNudge
            data-shop-country-nudge
            country={shopAddress.country}
          />
        </div>
      );
    }

    return null;
  }

  renderLocalPickupAvailableNudge() {
    if (!this.isLocalPickupAvailable()) return null;

    return (
      <ListingCardNudge localPickupAvailable />
    );
  }

  renderNudges() {
    const { listing, user } = this.props;
    if (isExperimentEnabled(user, experiments.MARKETPLACE_LISTING_CARD_NUDGES)) {
      return (
        <ListingCardNudge
          freeExpeditedShipping={this.isFreeExpeditedShipping()}
          freeShipping={this.isFreeShipping()}
          greatValue={this.isGreatValue()}
          inOtherCartsCount={listing?.otherBuyersWithListingInCartCounts}
          localPickupAvailable={this.isLocalPickupAvailable()}
          localPickupOnly={this.isLocalPickupOnly()}
          returnPolicyDays={this.returnPolicyDays()}
          displayMultiple
        />
      );
    }

    return (
      <>
        <ListingCardNudge
          freeExpeditedShipping={this.isFreeExpeditedShipping()}
          freeShipping={this.isFreeShipping()}
          localPickupOnly={this.isLocalPickupOnly()}
        />
        {this.renderLocalPickupAvailableNudge()}
        <InOtherCartsCardNudge listing={listing} inRedesignExperiment />
      </>
    );
  }

  render() {
    const { listing, user } = this.props;
    if (!listing) {
      return <PlaceholderCard />;
    }

    const redesign = this.props.redesignedCard;
    const classes = classNames(
      'grid-card',
      { 'grid-card--software': isDigital(listing) },
      { 'grid-card--discounted': this.hasDiscount() },
      { 'grid-card--redesign': redesign },
    );

    const openNewTab = user && user.deviceName === WEB;
    const images = listing.images.map(i => (i.source));

    return (
      <div className={classes}>
        <CoreLink
          className="grid-card__inner"
          to={this.listingUrl()}
          target={openNewTab ? '_blank' : undefined}
          clickEvent={this.clickEvent()}
        >
          {this.renderMetadata()}
          <div className="grid-card__main">
            <div className="grid-card__image">
              <ImageCarousel
                images={images}
                imgAlt={listing.title}
              />
              {!redesign &&
                <RibbonView
                  auction={isAuction(listing)}
                  listingStateDescription={listing.stateDescription}
                  listingStateSlug={listing.state}
                  discountDisplay={this.ribbonText()}
                />
              }
              {redesign && shouldShowBumpTag(this.props.listing) &&
                <div className="grid-card__bump-tag">
                  <BumpTag />
                </div>
              }
              {redesign && listing.state !== 'live' &&
                <div className="grid-card__sold">
                  {listing.stateDescription}
                </div>
              }
            </div>
            <div className="grid-card__main__text">
              {!redesign && shouldShowBumpTag(this.props.listing) &&
                <div className="grid-card__bump-tag">
                  <BumpTag />
                </div>
              }
              <h4 className="grid-card__title">
                {this.props.listing.title}
              </h4>
              {this.renderProximityShopLocation()}
            </div>
            {!redesign && <InOtherCartsCardNudge listing={this.props.listing} />}
          </div>
          <div className="grid-card__footer">
            {!redesign && this.renderShippingCallout()}
            {this.renderOriginalPrice()}
            <div
              className="grid-card__footer__pricing"
            >
              <div className="grid-card__price">
                <ListingPrice
                  combinedShippingDiscount={this.props.combinedShippingDiscount}
                  listing={listing}
                />
                {!redesign && this.renderAffirmCallout()}
              </div>
              <ListingCondition listing={listing} textOnly={this.props.redesignedCard} />
            </div>
            {this.props.displayAddToCart &&
              <div
                className="grid-card__footer__actions"
              >
                <AddToCartButton
                  listing={this.props.listing}
                  className="width-100"
                  position={this.props.position}
                  parentComponentName={this.props.trackingName}
                />
              </div>
            }
            {this.renderShopLocationCountryFlag()}
            {redesign && this.renderNudges()}
            {this.props.footerContent}
          </div>
        </CoreLink>
        <div className="grid-card__watch">
          <WatchBadgeWithRQL
            listing={this.props.listing}
            displayStyle="icon"
          />
        </div>
      </div>
    );
  }
}

function isListingDomesticToBuyer(sellerCountryCode?: string, buyerCountryCode?: string): boolean {
  if (!(sellerCountryCode && buyerCountryCode)) { return false; }
  return sellerCountryCode === buyerCountryCode;
}

function showShopCountryNudge(user: Partial<IUser>, location: Location) {
  if (user.countryCode !== 'US') {
    return optionalPathnameIncludes(location, '/marketplace') || isExperimentEnabled(user, experiments.LISTING_CARD_FLAG_EVERYWHERE);
  }

  return false;
}

function optionalPathnameIncludes(location: Location, path: string): boolean {
  if (!location?.pathname) {
    return false;
  }

  return location?.pathname?.includes(path);
}

function showProximityContent(shopAddress: Address, user: Partial<IUser>, location: Location) {
  const search = location?.search || '';
  return isUsingProximityFeatures(search) &&
    shopAddress?.country?.countryCode === 'US' &&
    user.countryCode === 'US';
}

function isUsingProximityFeatures(search: string) {
  return search.includes('proximity=true') || search.includes('local_pickup=true') || search.includes('sort=proximity');
}

function ListingPrice(props: {
  listing: ListingCardListingFields,
  combinedShippingDiscount: boolean,
}) {
  const { listing, combinedShippingDiscount } = props;

  if (combinedShippingDiscount) {
    return (
      <PriceWithShipping
        listing={listing}
        showCombinedRate
        useCombinedShippingStrikethrough
      />
    );
  }

  return (
    <PriceDisplay
      amountCents={listing.pricing.buyerPrice.amountCents}
      currency={listing.pricing.buyerPrice.currency}
      display={listing.pricing.buyerPrice.display}
      auction={isAuction(listing)}
    />
  );
}

function ListingCondition(props: { listing: ListingCardListingFields, textOnly: boolean; }) {
  const { listing, textOnly } = props;
  if (isDigital(listing)) return null;
  if (!listing.condition) return null;

  return (
    <div className="grid-card__condition">
      <ConditionDisplay
        slug={listing.condition.conditionSlug}
        displayName={listing.condition.displayName}
        textOnly={textOnly}
      />
    </div>
  );
}

function PlaceholderCard() {
  return (
    <div className="grid-card grid-card--placeholder">
      <div className="grid-card__inner">
        <div className="grid-card__main">
          <div className="grid-card__image" />
          <div className="grid-card__main__text">
            <div className="grid-card__title" />
          </div>
        </div>

        <div className="grid-card__footer">
          <div className="grid-card__footer__pricing">
            <div className="grid-card__price" />
          </div>
        </div>
      </div>
    </div>
  );
}

function isDigital(listing: ListingCardListingFields) {
  return listing.listingType === ListingType.DIGITAL;
}

function isAuction(listing: ListingCardListingFields) {
  return listing.listingType === ListingType.AUCTION;
}

export const ListingCardFragment = gql`
  fragment ListingCardFields on Listing {
    _id
    id
    title
    slug
    listingType
    upc
    state
    stateDescription
    bumped
    watching
    condition {
      displayName
      conditionSlug
      conditionUuid
    }
    pricing {
      buyerPrice {
        display
        currency
        amount
        amountCents
      }
      originalPrice {
        display
      }
      ribbon {
        display
      }
    }
    images(input: {
      transform: "card_square",
      count: 3,
      scope: "photos",
      type: "Product"
    }) {
      source
    }
    shipping {
      shippingPrices {
        shippingMethod
        carrierCalculated
        destinationPostalCodeNeeded
        rate {
          amount
          amountCents
          currency
          display
        }
      }
      freeExpeditedShipping
      localPickupOnly
      localPickup
    }
    shop {
      _id
      returnPolicy {
        usedReturnWindowDays
        newReturnWindowDays
      }
      address {
        locality
        region
        country {
          countryCode
          name
        }
      }
    }
    priceRecommendation {
      priceMiddle {
        amount
      }
    }
  }
`;

export default withRouter(withUserContext(ListingCard));
